'use client';

import { useLayoutEffect, useRef, useState } from 'react';
import { suggestMatchNote } from '@/lib/company-suggest-match';

/**
 * Row content for a single search hit. Shows the register suffix only when
 * the name + suffix fit on one line without truncating the name. The decision
 * is made by rendering an invisible probe that holds the full natural-width
 * content and comparing its width against the row's available width via a
 * ResizeObserver — so the suffix appears/disappears as the dropdown resizes.
 */
/** A row of /api/companies/suggest — what the dropdown shows and links to. */
export interface SuggestRow {
  euId: string;
  name: string;
  matchedFormerName?: string | null;
  matchedPerson?: string | null;
  city: string | null;
  registerId: string | null;
  registerType: string | null;
  registerCourt: string | null;
  faviconUrl: string | null;
}

export function ResultRowContent({
  name,
  note,
  suffix,
}: {
  name: string;
  note?: string | null;
  suffix: string | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLSpanElement>(null);
  const [showSuffix, setShowSuffix] = useState(false);

  useLayoutEffect(() => {
    if (!suffix) {
      return;
    }
    const container = containerRef.current;
    const probe = probeRef.current;
    if (!container || !probe) return;
    const measure = () => {
      setShowSuffix(probe.scrollWidth <= container.clientWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [name, note, suffix]);

  return (
    <div
      ref={containerRef}
      className="relative flex items-baseline gap-1 min-w-0 flex-1 overflow-hidden"
    >
      <span className="text-sm truncate text-left">
        {name}
        {note && <span className="text-muted-foreground"> ({note})</span>}
      </span>
      {showSuffix && suffix && (
        <span className="text-xs text-muted-foreground shrink-0">
          · {suffix}
        </span>
      )}
      {suffix && (
        <span
          ref={probeRef}
          aria-hidden
          className="invisible absolute left-0 top-0 whitespace-nowrap pointer-events-none"
        >
          <span className="text-sm">
            {name}
            {note && <span> ({note})</span>}
          </span>
          <span className="text-xs"> · {suffix}</span>
        </span>
      )}
    </div>
  );
}

/**
 * The muted " (Old Name)" / " (Person Name)" after a suggestion's name, for
 * rows that matched a former name or a linked person; nothing otherwise.
 * Not bold even inside a bold name span; truncates with it.
 */
export function MatchNote({
  row,
}: {
  row: Pick<SuggestRow, 'matchedFormerName' | 'matchedPerson'>;
}) {
  const note = suggestMatchNote(row);
  return note ? (
    <span className="font-normal text-muted-foreground"> ({note})</span>
  ) : null;
}
